import type { PatchOp, PresetModel } from "@defibrillator/preset-core";
import type { VitalTargetVersion } from "@defibrillator/vital-adapter";
import type { GenerationSseEvent } from "../../shared/types.js";

export type RefinePresetResult = {
  model: PresetModel;
  ops: PatchOp[];
  generationId: string;
  presetId?: string;
  slug?: string;
  versionId?: string;
  contentHash?: string;
  vitalVersion?: VitalTargetVersion;
  jobId?: string;
};

export class RefinePresetError extends Error {
  readonly code?: string;
  readonly currentVersionId?: string | null;

  constructor(
    message: string,
    opts?: { code?: string; currentVersionId?: string | null },
  ) {
    super(message);
    this.name = "RefinePresetError";
    this.code = opts?.code;
    this.currentVersionId = opts?.currentVersionId;
  }
}

/** Consume POST /api/presets/:id/refine SSE until done or error. */
export async function refinePreset(params: {
  presetId: string;
  baseVersionId: string;
  instruction: string;
  idempotencyKey: string;
  onEvent?: (event: GenerationSseEvent) => void;
  signal?: AbortSignal;
}): Promise<RefinePresetResult> {
  const res = await fetch(`/api/presets/${params.presetId}/refine`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      instruction: params.instruction,
      baseVersionId: params.baseVersionId,
      idempotencyKey: params.idempotencyKey,
    }),
    signal: params.signal,
  });

  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as {
      currentVersionId?: string | null;
    };
    throw new RefinePresetError("version_conflict", {
      code: "version_conflict",
      currentVersionId: body.currentVersionId ?? null,
    });
  }

  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => ({}));
    const message =
      typeof body === "object" && body && "error" in body
        ? String((body as { error: unknown }).error)
        : `refine_failed_${res.status}`;
    throw new RefinePresetError(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let latestOps: PatchOp[] = [];
  let latestModel: PresetModel | null = null;
  let generationId = "";
  let jobId: string | undefined;
  let presetId: string | undefined;
  let slug: string | undefined;
  let versionId: string | undefined;
  let contentHash: string | undefined;
  let vitalVersion: VitalTargetVersion | undefined;
  let streamError: RefinePresetError | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() ?? "";
    for (const chunk of chunks) {
      const dataLine = chunk.split("\n").find((l) => l.startsWith("data:"));
      if (!dataLine) continue;
      const event = JSON.parse(dataLine.slice(5).trim()) as GenerationSseEvent;
      params.onEvent?.(event);
      if (event.type === "ops") {
        latestOps = event.ops;
        generationId = event.generationId;
      }
      if (event.type === "job") {
        jobId = event.jobId;
        generationId = event.generationId;
      }
      if (event.type === "done") {
        latestModel = event.model;
        generationId = event.generationId;
        presetId = event.presetId;
        slug = event.slug;
        versionId = event.versionId;
        contentHash = event.contentHash;
        vitalVersion = event.vitalVersion;
      }
      if (event.type === "error") {
        streamError = new RefinePresetError(event.message, {
          code: event.code,
          currentVersionId: event.currentVersionId,
        });
        generationId = event.generationId ?? generationId;
      }
      if (event.type === "status" || event.type === "policy") {
        generationId = event.generationId;
      }
    }
  }

  if (streamError) throw streamError;
  if (!latestModel) {
    throw new RefinePresetError("refine_incomplete");
  }

  return {
    model: latestModel,
    ops: latestOps,
    generationId,
    presetId,
    slug,
    versionId,
    contentHash,
    vitalVersion,
    jobId,
  };
}
